import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Upload, X } from "lucide-react";
import { teststepsService } from "../../services/teststepsService";
import s3UploadService from "../../services/s3UploadService";
import { getStoredClientId } from "../../utils/tokenUtils";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

const AddResultModal = ({
  open,
  onClose,
  testCase,
  testRunId,
  projectId,
  onSave,
  isTestRunCompleted = false,
}) => {
  const { user } = useAuth();
  const [status, setStatus] = useState("pending");
  const [comment, setComment] = useState("");
  const [timeSpent, setTimeSpent] = useState("");
  const [defects, setDefects] = useState("");
  const [testSteps, setTestSteps] = useState([]);
  const [stepStatuses, setStepStatuses] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    if (open && testCase?.test_case_id) {
      loadTestSteps();
      // Reset form
      setStatus("pending");
      setComment("");
      setTimeSpent("");
      setDefects("");
      setStepStatuses({});
      setAttachments([]);
    }
  }, [open, testCase]);

  const loadTestSteps = async () => {
    if (!testCase?.test_case_id) return;
    try {
      setLoadingSteps(true);
      const response = await teststepsService.getTestSteps(
        testCase.test_case_id,
      );
      let steps = [];
      if (response?.success && response?.data?.test_steps) {
        steps = response.data.test_steps;
      } else if (response?.data?.test_steps) {
        steps = response.data.test_steps;
      } else if (response?.test_steps) {
        steps = response.test_steps;
      } else if (Array.isArray(response?.data)) {
        steps = response.data;
      } else if (Array.isArray(response)) {
        steps = response;
      }
      setTestSteps(steps);
      // Initialize step statuses
      const initialStatuses = {};
      steps.forEach((step, index) => {
        initialStatuses[step.step_id || index] = "untested";
      });
      setStepStatuses(initialStatuses);
    } catch (error) {
      console.error("Error loading test steps:", error);
      setTestSteps([]);
    } finally {
      setLoadingSteps(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file size (50 MB max)
    const maxSize = 50 * 1024 * 1024;
    const invalidFiles = files.filter((file) => file.size > maxSize);
    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed 50 MB limit. Max file size: 50 MB`);
      return;
    }

    // Validate total files (10 max)
    if (attachments.length + files.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }

    try {
      setUploadingFiles(true);
      const clientId = getStoredClientId() || user?.client_id;

      if (!clientId) {
        toast.error("Client ID is required for file uploads");
        setUploadingFiles(false);
        return;
      }

      const uploadedUrls = await s3UploadService.uploadFiles(
        files,
        "test-run-results",
        clientId,
        projectId,
      );

      const newAttachments = uploadedUrls.map((url, index) => ({
        url,
        fileName: files[index].name,
        type: files[index].type,
        size: files[index].size,
        uploadedAt: new Date().toISOString(),
      }));

      setAttachments((prev) => [...prev, ...newAttachments]);
      toast.success(`${files.length} file(s) uploaded successfully`);
      e.target.value = "";
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(error.message || "Failed to upload files");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!status || status === "") {
      toast.error("Please select a status");
      return;
    }

    try {
      setLoading(true);
      const resultData = {
        execution_status: status,
        notes: comment || null,
        actual_hours: timeSpent ? parseFloat(timeSpent) : null,
        result_attachments: attachments,
        step_statuses: stepStatuses, // Include step-by-step statuses
      };

      await onSave(resultData);
      toast.success("Result added successfully");
      onClose();
    } catch (error) {
      console.error("Error saving result:", error);
      toast.error(error.response?.data?.message || "Failed to save result");
    } finally {
      setLoading(false);
    }
  };

  const fieldShell =
    "mt-1.5 border-stone-200 bg-stone-50/80 shadow-none focus-visible:ring-[#a17561]/30";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto gap-0 border-stone-200 p-0 sm:rounded-xl">
        <DialogHeader className="bg-[#a17561] text-white px-5 py-4 sm:rounded-t-xl border-b border-[#8f6453]">
          <DialogTitle className="text-white text-lg font-semibold tracking-tight">
            Add result
          </DialogTitle>
          {testCase?.title ? (
            <p className="text-sm text-white/90 font-normal leading-snug pt-1 line-clamp-2">
              {testCase.title}
            </p>
          ) : null}
        </DialogHeader>

        <div className="px-5 py-5 grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,340px)] gap-6 lg:gap-8">
          <div className="space-y-5 min-w-0">
            <div>
              <Label
                htmlFor="status"
                className="text-xs font-medium uppercase tracking-wide text-stone-500"
              >
                Status <span className="text-red-500 normal-case">*</span>
              </Label>
              <Select
                value={status}
                onValueChange={setStatus}
                disabled={isTestRunCompleted}
              >
                <SelectTrigger id="status" className={`${fieldShell} h-10`}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="comment"
                className="text-xs font-medium uppercase tracking-wide text-stone-500"
              >
                Comment
              </Label>
              <Textarea
                id="comment"
                rows={5}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Describe what you observed, evidence, or next steps."
                disabled={isTestRunCompleted}
                className={`${fieldShell} resize-none min-h-[120px]`}
              />
            </div>

            {loadingSteps ? (
              <p className="text-sm text-stone-500 py-2">Loading steps…</p>
            ) : testSteps.length > 0 ? (
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide text-stone-500">
                  Step results
                </Label>
                <div className="mt-2 space-y-2.5 max-h-60 overflow-y-auto pr-1">
                  {testSteps.map((step, index) => {
                    const stepId = step.step_id || index;
                    const stepStatus = stepStatuses[stepId] || "untested";

                    return (
                      <div
                        key={stepId}
                        className="rounded-lg border border-stone-200/90 bg-white p-3 shadow-sm"
                      >
                        <div className="flex items-start gap-3 mb-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-100 text-xs font-semibold text-stone-700">
                            {step.step_number || index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-stone-800 leading-snug">
                              {step.action ||
                                step.description ||
                                step.step ||
                                "No action specified"}
                            </p>
                            {step.expected_result ? (
                              <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">
                                <span className="font-medium text-stone-600">
                                  Expected:
                                </span>{" "}
                                {step.expected_result}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <Select
                          value={stepStatus}
                          onValueChange={(value) => {
                            setStepStatuses((prev) => ({
                              ...prev,
                              [stepId]: value,
                            }));
                          }}
                          disabled={isTestRunCompleted}
                        >
                          <SelectTrigger
                            className={`${fieldShell} h-9 text-xs w-full`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="untested">Untested</SelectItem>
                            <SelectItem value="passed">Passed</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          <aside className="space-y-5 rounded-xl border border-stone-200/90 bg-stone-50/50 p-4 lg:p-5 h-fit">
            <p className="text-xs font-medium uppercase tracking-wide text-stone-500 border-b border-stone-200/80 pb-2">
              Details &amp; files
            </p>

            <div>
              <Label htmlFor="time-spent" className="text-sm text-stone-700">
                Time spent
              </Label>
              <Input
                id="time-spent"
                type="text"
                value={timeSpent}
                onChange={(e) => setTimeSpent(e.target.value)}
                placeholder="e.g. 0.5h or 30m"
                disabled={isTestRunCompleted}
                className={fieldShell}
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <Label htmlFor="defects" className="text-sm text-stone-700">
                  Defects
                </Label>
                <button
                  type="button"
                  className="text-xs font-medium text-[#a17561] hover:text-[#825e4e] underline-offset-2 hover:underline"
                  onClick={() => {
                    toast("Push defects functionality coming soon");
                  }}
                >
                  Push
                </button>
              </div>
              <Textarea
                id="defects"
                rows={3}
                value={defects}
                onChange={(e) => setDefects(e.target.value)}
                placeholder="Bug tracker IDs, one per line."
                disabled={isTestRunCompleted}
                className={`${fieldShell} resize-none`}
              />
            </div>

            <div>
              <Label htmlFor="attachment" className="text-sm text-stone-700">
                Attachments
              </Label>
              <input
                accept="image/*,application/pdf,.doc,.docx"
                className="hidden"
                id="attachment"
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploadingFiles || isTestRunCompleted}
              />
              <label htmlFor="attachment" className="block mt-1.5">
                <span
                  className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-stone-300 bg-white px-3 py-2.5 text-sm font-medium text-stone-600 transition-colors hover:border-[#a17561]/50 hover:bg-stone-50 ${
                    uploadingFiles || isTestRunCompleted
                      ? "pointer-events-none opacity-50"
                      : ""
                  }`}
                >
                  <Upload className="h-4 w-4 shrink-0 opacity-70" />
                  {uploadingFiles ? "Uploading…" : "Choose files"}
                </span>
              </label>
              <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">
                Up to 10 files · 50 MB each · images, PDF, Word
              </p>

              {attachments.length > 0 ? (
                <ul className="mt-3 space-y-1.5">
                  {attachments.map((attachment, index) => (
                    <li
                      key={`${attachment.fileName}-${index}`}
                      className="flex items-center gap-2 rounded-md border border-stone-200 bg-white px-2.5 py-2"
                    >
                      <span className="text-sm text-stone-800 truncate flex-1 min-w-0">
                        {attachment.fileName}
                      </span>
                      {!isTestRunCompleted ? (
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="shrink-0 rounded p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </aside>
        </div>

        <DialogFooter className="px-5 py-4 border-t border-stone-200 bg-stone-50/60 sm:rounded-b-xl gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-red-400/80 text-red-700 hover:bg-red-50"
          >
            Cancel
          </Button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || isTestRunCompleted}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium button-brown disabled:opacity-50 disabled:pointer-events-none min-w-[7.5rem]"
          >
            {loading ? "Saving…" : "Add result"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddResultModal;
